import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Check, ChevronDown, Circle } from "lucide-react";
import {
  ApprovalStatus,
  getApprovalStatusLabel,
} from "@employee-management/common";
import { apiService } from "@/lib/api-service";
import { useAuth } from "@/lib/auth-context";
import { UserRole } from "@employee-management/common";
import clsx from "clsx";

interface StatusDropdownProps {
  overtimeEntryId: string;
  currentStatus: ApprovalStatus;
  projectId: string;
  disabled?: boolean;
}

export function StatusDropdown({
  overtimeEntryId,
  currentStatus,
  projectId,
  disabled = false,
}: StatusDropdownProps) {
  const [isUpdating, setIsUpdating] = useState(false);
  const [showRejectionDialog, setShowRejectionDialog] = useState(false);
  const [rejectionComment, setRejectionComment] = useState("");
  const [pendingStatus, setPendingStatus] = useState<ApprovalStatus | null>(
    null
  );
  const queryClient = useQueryClient();
  const { hasRole } = useAuth();

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case ApprovalStatus.PENDING:
        return "secondary";
      case ApprovalStatus.APPROVED:
        return "default";
      case ApprovalStatus.REJECTED:
        return "destructive";
      default:
        return "secondary";
    }
  };

  const handleStatusChange = async (
    newStatus: ApprovalStatus,
    comment?: string
  ) => {
    if (isUpdating || disabled) return;

    setIsUpdating(true);
    try {
      await apiService.patch(`/overtime/${overtimeEntryId}/approval`, {
        approvalStatus: newStatus,
        ...(newStatus === ApprovalStatus.REJECTED && {
          rejectionComment: comment || "Status changed via dropdown",
        }),
      });

      // Invalidate and refetch relevant queries
      await queryClient.invalidateQueries({
        queryKey: ["overtime", "project", projectId],
      });
      await queryClient.invalidateQueries({
        queryKey: ["projects"],
      });
      await queryClient.invalidateQueries({
        queryKey: ["project", projectId],
      });
      await queryClient.invalidateQueries({
        queryKey: ["project-revision-history", projectId],
      });

      toast.success(`Status updated to ${getApprovalStatusLabel(newStatus)}`);
      setShowRejectionDialog(false);
      setRejectionComment("");
      setPendingStatus(null);
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleStatusSelect = (status: ApprovalStatus) => {
    if (status === ApprovalStatus.REJECTED) {
      setPendingStatus(status);
      setShowRejectionDialog(true);
    } else {
      handleStatusChange(status);
    }
  };

  const handleRejectionConfirm = () => {
    if (!rejectionComment.trim()) {
      toast.error("Rejection comment is required");
      return;
    }
    if (pendingStatus) {
      handleStatusChange(pendingStatus, rejectionComment);
    }
  };

  const handleRejectionCancel = () => {
    setShowRejectionDialog(false);
    setRejectionComment("");
    setPendingStatus(null);
  };

  // Only managers can edit status
  if (!hasRole(UserRole.MANAGER) || disabled) {
    return (
      <Badge variant={getStatusBadgeVariant(currentStatus)}>
        {getApprovalStatusLabel(currentStatus)}
      </Badge>
    );
  }

  const getStatusIconColor = (status: ApprovalStatus) => {
    switch (status) {
      case ApprovalStatus.REJECTED:
        return "text-destructive";
      case ApprovalStatus.APPROVED:
        return "text-green-500";
      case ApprovalStatus.PENDING:
      default:
        return "text-gray-500";
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger>
          <Badge
            variant={getStatusBadgeVariant(currentStatus)}
            className="cursor-pointer hover:opacity-80 transition-opacity"
          >
            {getApprovalStatusLabel(currentStatus)}
            <ChevronDown className="h-3 w-3 ml-1" />
          </Badge>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          {Object.values(ApprovalStatus).map((status) => (
            <DropdownMenuItem
              key={status}
              onClick={() => handleStatusSelect(status)}
              className="cursor-pointer hover:bg-gray-100"
              disabled={status === currentStatus}
            >
              <Circle
                className={clsx("h-4 w-4 mr-2", getStatusIconColor(status))}
                fill="currentColor"
              />
              {getApprovalStatusLabel(status)}

              <span className="ml-auto" />
              {status === currentStatus && <Check className="h-4 w-4 ml-4" />}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={showRejectionDialog} onOpenChange={setShowRejectionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Overtime Entry</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting this overtime entry.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              placeholder="Enter rejection reason..."
              value={rejectionComment}
              onChange={(e) => setRejectionComment(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleRejectionCancel}
              disabled={isUpdating}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRejectionConfirm}
              disabled={isUpdating || !rejectionComment.trim()}
            >
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
